import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import { PencilFill, CheckLg, XLg, PlusCircle, Trash } from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/SecurityContext';
import RoleGuard from '../security/RoleGuard';
import SectionControls from '../SectionControls';
import { updateInfoCardsSection } from '../../services/contentService';
import './InfoCardsBlock.scss';

const ACCENT_OPTIONS = [
  { value: 'top', label: 'Top accent' },
  { value: 'left', label: 'Left accent' },
];

const COLUMN_OPTIONS = [
  { value: 2, label: '2 columns' },
  { value: 3, label: '3 columns' },
];

function defaultCard() {
  return {
    title: 'Card title',
    description: 'One or two short sentences.',
  };
}

export function defaultInfoCardsData() {
  return {
    title: 'Section title',
    intro: '',
    accent: 'top',
    columns: 2,
    cards: [defaultCard(), defaultCard(), defaultCard(), defaultCard()],
    footnote: '',
  };
}

export function InfoCardsView({ data }) {
  const cards = Array.isArray(data?.cards) ? data.cards : [];
  const accent = data?.accent === 'left' ? 'left' : 'top';
  const columns = data?.columns === 3 ? 3 : 2;
  const colSpan = columns === 3 ? 4 : 6; // bootstrap 12-col grid
  return (
    <div className={`info-cards-block info-cards-block--${accent}`}>
      {data?.title ? <h2 className="info-cards-block__title">{data.title}</h2> : null}
      {data?.intro ? <p className="info-cards-block__intro">{data.intro}</p> : null}
      <Row className="info-cards-block__cards g-3">
        {cards.map((card, idx) => (
          <Col key={idx} md={colSpan} className="info-cards-block__card-col">
            <div className="info-cards-block__card">
              {/* Optional eyebrow (e.g. the partner a card belongs to). */}
              {card.eyebrow ? (
                <div className="info-cards-block__card-eyebrow">{card.eyebrow}</div>
              ) : null}
              <div className="info-cards-block__card-title">{card.title}</div>
              <div className="info-cards-block__card-description">{card.description}</div>
            </div>
          </Col>
        ))}
      </Row>
      {/* Optional note rendered underneath the grid. */}
      {data?.footnote ? <p className="info-cards-block__footnote">{data.footnote}</p> : null}
    </div>
  );
}

function InfoCardsBlock({
  pageId, section, onMoveUp, onMoveDown, onDelete, isFirstSection, isLastSection,
}) {
  const { user } = useAuth();
  const initialData = useMemo(() => section?.data || defaultInfoCardsData(), [section]);
  const [data, setData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setData(section?.data || defaultInfoCardsData());
  }, [section]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const modifiedBy = user?.attributes?.email || user?.username || 'user';
      await updateInfoCardsSection(pageId, section.position, data, modifiedBy);
      setIsEditing(false);
    } catch (e) {
      console.error('Error saving info cards:', e);
      // eslint-disable-next-line no-alert
      alert('Failed to save section. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() {
    setData(section?.data || defaultInfoCardsData());
    setIsEditing(false);
  }

  function updateField(field, value) {
    setData((prev) => ({ ...prev, [field]: value }));
  }

  function updateCard(idx, patch) {
    setData((prev) => {
      const next = { ...prev, cards: [...(prev.cards || [])] };
      next.cards[idx] = { ...next.cards[idx], ...patch };
      return next;
    });
  }

  function addCard() {
    setData((prev) => ({ ...prev, cards: [...(prev.cards || []), defaultCard()] }));
  }

  function removeCard(idx) {
    setData((prev) => {
      const next = { ...prev, cards: [...(prev.cards || [])] };
      next.cards.splice(idx, 1);
      return next;
    });
  }

  if (isEditing) {
    return (
      <div className="info-cards-block info-cards-block--editing">
        <Row className="g-2 mb-3">
          <Col md={6}>
            <Form.Label>Section title</Form.Label>
            <Form.Control
              type="text"
              value={data.title || ''}
              onChange={(e) => updateField('title', e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label>Accent</Form.Label>
            <Form.Select
              value={data.accent || 'top'}
              onChange={(e) => updateField('accent', e.target.value)}
            >
              {ACCENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label>Columns</Form.Label>
            <Form.Select
              value={data.columns || 2}
              onChange={(e) => updateField('columns', parseInt(e.target.value, 10))}
            >
              {COLUMN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
          </Col>
          <Col md={12}>
            <Form.Label>Intro paragraph (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={data.intro || ''}
              onChange={(e) => updateField('intro', e.target.value)}
            />
          </Col>
        </Row>

        {(data.cards || []).map((card, idx) => (
          <div key={idx} className="info-cards-block__editor-card">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Card {idx + 1}</strong>
              <Button variant="outline-danger" size="sm" onClick={() => removeCard(idx)}>
                <Trash /> Remove
              </Button>
            </div>
            <Row className="g-2">
              <Col md={3}>
                <Form.Label>Eyebrow (optional)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. EPRI"
                  value={card.eyebrow || ''}
                  onChange={(e) => updateCard(idx, { eyebrow: e.target.value })}
                />
              </Col>
              <Col md={4}>
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  value={card.title || ''}
                  onChange={(e) => updateCard(idx, { title: e.target.value })}
                />
              </Col>
              <Col md={5}>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={card.description || ''}
                  onChange={(e) => updateCard(idx, { description: e.target.value })}
                />
              </Col>
            </Row>
          </div>
        ))}

        <Button variant="outline-primary" size="sm" onClick={addCard} className="mb-3">
          <PlusCircle /> Add card
        </Button>

        <Form.Group className="mb-2">
          <Form.Label>Note below the cards (optional)</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="e.g. Follow-on opportunities are scoped case by case with the relevant partner."
            value={data.footnote || ''}
            onChange={(e) => updateField('footnote', e.target.value)}
          />
        </Form.Group>

        <div className="info-cards-block__editor-actions">
          <Button variant="success" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</>
            ) : (
              <><CheckLg /> Save</>
            )}
          </Button>
          <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
            <XLg /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="info-cards-block info-cards-block--view">
      <InfoCardsView data={data} />
      <RoleGuard role="admin">
        <div className="admin-controls">
          <Button
            variant="outline-secondary"
            size="sm"
            className="edit-btn"
            onClick={() => setIsEditing(true)}
          >
            <PencilFill /> Edit cards
          </Button>
          <SectionControls
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onDelete={onDelete}
            isFirstSection={isFirstSection}
            isLastSection={isLastSection}
          />
        </div>
      </RoleGuard>
    </div>
  );
}

export default InfoCardsBlock;
